/**
 * ConnectionHistory Component
 * Feature: 080-critique-sharing
 *
 * Displays detailed interaction history between two users.
 * Shows all edges in both directions with interaction types and counts.
 */

import {
  useConnectionHistory,
  EdgeData,
  formatInteractionType,
} from '../../graphql/hooks/useEngagementGraphGraphQL';

interface ConnectionHistoryProps {
  userAId: string;
  userBId?: string;
  userBEmail?: string;
  onClose?: () => void;
}

export function ConnectionHistory({
  userAId,
  userBId,
  userBEmail,
  onClose,
}: ConnectionHistoryProps) {
  const { data, isLoading, isError, error, refetch } = useConnectionHistory(
    userAId,
    userBId,
    userBEmail
  );

  if (isLoading) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-surface-2 rounded w-1/2"></div>
          <div className="h-32 bg-surface-2 rounded"></div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <div className="text-center text-red-500">
          <p className="font-medium">Failed to load connection history</p>
          <p className="text-sm mt-1">{error?.message || 'Unknown error'}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <p className="text-muted text-center">No connection history found</p>
      </div>
    );
  }

  const { userA, userB, outboundEdges, inboundEdges, totalOutbound, totalInbound } = data;

  // Sort edges by last interaction (most recent first)
  const sortedOutbound = [...outboundEdges].sort(
    (a, b) => new Date(b.lastInteractionAt).getTime() - new Date(a.lastInteractionAt).getTime()
  );
  const sortedInbound = [...inboundEdges].sort(
    (a, b) => new Date(b.lastInteractionAt).getTime() - new Date(a.lastInteractionAt).getTime()
  );

  return (
    <div className="bg-surface-1 rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Connection History</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-muted hover:text-foreground transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Users involved */}
      <div className="flex items-center justify-between mb-6 p-4 bg-surface-2 rounded-lg">
        <UserCard
          label="User A"
          username={userA.username}
          displayName={userA.displayName}
          isPending={false}
        />
        <div className="flex flex-col items-center gap-1">
          <div className="flex items-center gap-2 text-muted">
            <span className="text-foreground font-medium">{totalOutbound}</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-foreground font-medium">{totalInbound}</span>
          </div>
        </div>
        <UserCard
          label={userB.isPending ? 'Pending User' : 'User B'}
          username={userB.isPending ? userB.email || 'Unknown' : userB.username}
          displayName={userB.displayName}
          isPending={userB.isPending || false}
        />
      </div>

      {/* Timeline summary */}
      <div className="mb-6 text-sm text-muted">
        <span>First interaction: </span>
        <span className="text-foreground">
          {new Date(data.firstInteractionAt).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
        <span className="mx-2">|</span>
        <span>Last interaction: </span>
        <span className="text-foreground">
          {new Date(data.lastInteractionAt).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      </div>

      {/* Edges breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Outbound edges */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-3">
            {userA.username} → {userB.isPending ? userB.email : userB.username}
          </h4>
          {sortedOutbound.length === 0 ? (
            <p className="text-muted text-sm">No outbound interactions</p>
          ) : (
            <div className="space-y-2">
              {sortedOutbound.map((edge) => (
                <EdgeCard key={edge.globalId} edge={edge} />
              ))}
            </div>
          )}
        </div>

        {/* Inbound edges */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-3">
            {userB.isPending ? userB.email : userB.username} → {userA.username}
          </h4>
          {sortedInbound.length === 0 ? (
            <p className="text-muted text-sm">No inbound interactions</p>
          ) : (
            <div className="space-y-2">
              {sortedInbound.map((edge) => (
                <EdgeCard key={edge.globalId} edge={edge} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface UserCardProps {
  label: string;
  username: string | null;
  displayName: string | null;
  isPending: boolean;
}

function UserCard({ label, username, displayName, isPending }: UserCardProps) {
  return (
    <div className="text-center">
      <div className="text-xs text-muted mb-1">{label}</div>
      <div className="w-12 h-12 rounded-full bg-surface flex items-center justify-center mx-auto mb-2">
        <span className="text-lg text-muted">
          {isPending ? '@' : (username?.[0]?.toUpperCase() || '?')}
        </span>
      </div>
      <div className="text-foreground font-medium">{username || 'Unknown'}</div>
      {displayName && <div className="text-xs text-muted">{displayName}</div>}
      {isPending && (
        <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-500">
          Pending
        </span>
      )}
    </div>
  );
}

interface EdgeCardProps {
  edge: EdgeData;
}

function EdgeCard({ edge }: EdgeCardProps) {
  const firstDate = new Date(edge.firstInteractionAt);
  const lastDate = new Date(edge.lastInteractionAt);

  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });

  return (
    <div className="bg-surface p-3 rounded-lg border border-border/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-foreground font-medium">
            {formatInteractionType(edge.interactionType)}
          </span>
        </div>
        <span className="bg-primary/20 text-primary text-sm font-medium px-2 py-0.5 rounded">
          {edge.interactionCount}x
        </span>
      </div>
      <div className="text-xs text-muted">
        {edge.interactionCount === 1 ? (
          <span>{formatDate(firstDate)}</span>
        ) : (
          <span>
            {formatDate(firstDate)} - {formatDate(lastDate)}
          </span>
        )}
      </div>
    </div>
  );
}
